import { useCallback, useEffect, useRef, useState } from 'react'

export type StripMode = 'row' | 'grid'

/**
 * Banda de incidencias redimensionable.
 *
 * Arrastrar la manija hacia abajo agranda la banda y, pasada ~1,35 filas,
 * cada grupo deja de ser una fila con scroll horizontal y pasa a ser una
 * grilla que envuelve: más tarjetas a la vista sin scrollear.
 */
export function useResizableStrip() {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [height, setHeight] = useState<number | null>(null)
  const [mode, setMode] = useState<StripMode>('row')
  const drag = useRef<{ y: number; h: number } | null>(null)

  const rowHeight = useCallback(() => {
    // Una tarjeta de un grupo contraído mide 0: se mide una visible.
    const card = scrollRef.current?.querySelector(
      '.group-body:not([hidden]) .card-mini',
    ) as HTMLElement | null
    return (card?.offsetHeight || 112) + 8
  }, [])

  const apply = useCallback(
    (px: number) => {
      const base = rowHeight()
      const max = Math.round(window.innerHeight * 0.7)
      const next = Math.max(base, Math.min(max, Math.round(px)))
      setHeight(next)
      setMode(next > base * 1.35 ? 'grid' : 'row')
    },
    [rowHeight],
  )

  const reset = useCallback(() => {
    setHeight(null)
    setMode('row')
  }, [])

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const h = scrollRef.current?.getBoundingClientRect().height ?? 0
    drag.current = { y: e.clientY, h }
    e.currentTarget.setPointerCapture(e.pointerId)
    e.preventDefault()
  }, [])

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!drag.current) return
      apply(drag.current.h + (e.clientY - drag.current.y))
    },
    [apply],
  )

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    drag.current = null
    try {
      e.currentTarget.releasePointerCapture(e.pointerId)
    } catch {
      /* el puntero ya se soltó */
    }
  }, [])

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const current = height ?? scrollRef.current?.getBoundingClientRect().height ?? 0
      if (e.key === 'ArrowDown') {
        apply(current + 48)
        e.preventDefault()
      } else if (e.key === 'ArrowUp') {
        apply(current - 48)
        e.preventDefault()
      } else if (e.key === 'Home') {
        reset()
        e.preventDefault()
      }
    },
    [apply, height, reset],
  )

  // Desde 1024 px el riel ya es una lista vertical que ocupa la columna:
  // una altura fija heredada del arrastre lo rompería.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)')
    const sync = () => {
      if (mq.matches) reset()
    }
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [reset])

  return {
    scrollRef,
    mode,
    style: height === null ? undefined : { height: `${height}px` },
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onKeyDown,
      onDoubleClick: reset,
    },
  }
}
